import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, GitMerge, Loader2, Search } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import {
  getMessageMatchCandidates,
  mergeCommLogEntry,
  type CommLogEntry,
  type PatientMatchCandidate,
} from '@/services/communications.service';
import { searchPatients } from '@/services/patients.service';
import { getPlansByPatient, type PlanListItem } from '@/services/plans.service';
import type { PatientSummary } from '@/types/patient.types';

interface PickedPatient {
  patientId: number;
  displayId: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  cellPhone: string | null;
  birthDate: string | null;
}

function fromCandidate(c: PatientMatchCandidate): PickedPatient {
  return {
    patientId: c.patientId,
    displayId: c.displayId != null ? String(c.displayId) : null,
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email,
    cellPhone: c.cellPhone,
    birthDate: c.birthDate,
  };
}

function fromSummary(p: PatientSummary): PickedPatient {
  return {
    patientId: p.patientId,
    displayId: p.displayId,
    firstName: p.firstName,
    lastName: p.lastName,
    email: p.email,
    cellPhone: p.cellPhone ?? p.phone ?? null,
    birthDate: p.dateOfBirth,
  };
}

function fullName(p: { firstName: string; lastName: string }): string {
  const name = [p.lastName, p.firstName].filter(Boolean).join(', ').trim();
  return name || 'Unknown';
}

function matchBackground(score: number): string {
  if (score >= 4) return 'bg-emerald-100';
  if (score === 3) return 'bg-emerald-50';
  if (score === 2) return 'bg-green-50';
  if (score === 1) return 'bg-lime-50';
  return 'bg-slate-50';
}

function formatPlanDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

interface MessageMergeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entry: CommLogEntry | null;
  onMerged?: (updated: CommLogEntry) => void;
}

type Step = 'patient' | 'plan';

export function MessageMergeDialog({ isOpen, onClose, entry, onMerged }: MessageMergeDialogProps) {
  const [step, setStep] = useState<Step>('patient');
  const [candidates, setCandidates] = useState<PatientMatchCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<PatientSummary[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [picked, setPicked] = useState<PickedPatient | null>(null);
  // PlanListItem, which is what getPlansByPatient returns — same mix-up
  // LeadMergeDialog had.
  const [plans, setPlans] = useState<PlanListItem[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planId, setPlanId] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);

  const logId = entry?.commLogId ?? null;
  const searchDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('patient');
      setCandidates([]);
      setSearchTerm('');
      setSearchResults([]);
      setPicked(null);
      setPlans([]);
      setPlanId(0);
      setSubmitting(false);
      return;
    }

    if (logId == null) return;

    setCandidatesLoading(true);
    getMessageMatchCandidates(logId)
      .then((res) => {
        setCandidates(res.candidates);
      })
      .catch(() => {
        // Quietly degrade; the manual search below still works.
        setCandidates([]);
      })
      .finally(() => setCandidatesLoading(false));
  }, [isOpen, logId]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchDebounceRef.current = window.setTimeout(() => {
      searchPatients(term)
        .then((rows) => setSearchResults(rows))
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 250);

    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchTerm]);

  const handlePickPatient = useCallback((next: PickedPatient) => {
    setPicked(next);
    setPlanId(0);
    setStep('plan');

    setPlansLoading(true);
    getPlansByPatient(next.patientId)
      .then((rows) => setPlans(rows))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  const handleMerge = useCallback(async () => {
    if (logId == null || picked == null) return;

    setSubmitting(true);
    try {
      const updated = await mergeCommLogEntry(logId, {
        patientId: picked.patientId,
        planId: planId || 0,
      });
      toast.success('Merged', `Message linked to ${fullName(picked)}.`);
      onMerged?.(updated);
      onClose();
    } catch (error) {
      toast.error('Merge failed', getErrorMessage(error, 'Unable to merge message.'));
    } finally {
      setSubmitting(false);
    }
  }, [logId, onClose, onMerged, picked, planId]);

  const subjectPreview = useMemo(() => {
    if (!entry) return '';
    const raw = (entry.subject || entry.message || '').trim();
    return raw.length > 80 ? raw.slice(0, 80) + '…' : raw;
  }, [entry]);

  if (!entry) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={step === 'patient' ? 'Merge to Comm Log — Pick Patient' : 'Merge to Comm Log — Pick Plan'}
      size="xl"
      footer={
        step === 'plan' ? (
          <>
            <button
              type="button"
              onClick={() => setStep('patient')}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              type="button"
              onClick={() => void handleMerge()}
              disabled={submitting || picked == null}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />}
              Merge
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        )
      }
    >
      {subjectPreview && (
        <div className="mb-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
          <span className="font-medium text-slate-700">Message:</span> {subjectPreview}
        </div>
      )}

      {step === 'patient' && (
        <div className="flex flex-col gap-5">
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Possible Matches</h3>
            {candidatesLoading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner size="sm" />
              </div>
            ) : candidates.length === 0 ? (
              <p className="rounded-md bg-slate-50 px-3 py-3 text-sm text-slate-500">No matches found.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {candidates.map((c) => (
                  <li key={c.patientId}>
                    <button
                      type="button"
                      onClick={() => handlePickPatient(fromCandidate(c))}
                      className={`group flex w-full flex-col gap-1 rounded-md border border-slate-200 px-3 py-2 text-left transition-colors hover:border-blue-400 hover:bg-blue-50 ${matchBackground(c.matchScore)}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-900">
                          {fullName(c)}{c.displayId ? ` (#${c.displayId})` : ''}
                        </span>
                        {c.matchScore > 0 && (
                          <span className="text-xs text-slate-500">match score {c.matchScore}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
                        {c.email && (
                          <span className={c.matches.email ? 'font-semibold text-emerald-700' : ''}>
                            {c.matches.email && <Check className="mr-0.5 inline h-3 w-3" />}
                            {c.email}
                          </span>
                        )}
                        {c.cellPhone && (
                          <span className={c.matches.phone ? 'font-semibold text-emerald-700' : ''}>
                            {c.matches.phone && <Check className="mr-0.5 inline h-3 w-3" />}
                            {c.cellPhone}
                          </span>
                        )}
                        {c.birthDate && <span>DOB {c.birthDate}</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Search Patients</h3>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type at least 2 characters to search..."
                className="w-full rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {searchTerm.trim().length >= 2 && (
              <div className="mt-2">
                {searchLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <p className="rounded-md bg-slate-50 px-3 py-3 text-sm text-slate-500">No patients found.</p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {searchResults.map((p) => (
                      <li key={p.patientId}>
                        <button
                          type="button"
                          onClick={() => handlePickPatient(fromSummary(p))}
                          className="flex w-full flex-col gap-0.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-left hover:border-blue-400 hover:bg-blue-50"
                        >
                          <span className="text-sm font-semibold text-slate-900">
                            {fullName(p)}{p.displayId ? ` (#${p.displayId})` : ''}
                          </span>
                          <span className="text-xs text-slate-500">
                            {[p.email, p.cellPhone || p.phone].filter(Boolean).join(' · ') || '—'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {step === 'plan' && picked && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div>
              <span className="block text-xs uppercase tracking-wide text-slate-500">Selected patient</span>
              <span className="text-sm font-semibold text-slate-900">
                {fullName(picked)}{picked.displayId ? ` (#${picked.displayId})` : ''}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setStep('patient')}
              className="text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              Change
            </button>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Plan</h3>
            {plansLoading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner size="sm" />
              </div>
            ) : (
              <table className="w-full overflow-hidden rounded-md border border-slate-200 text-sm">
                <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="w-10 px-3 py-2"></th>
                    <th className="px-3 py-2">Plan</th>
                    <th className="px-3 py-2">Last Contact</th>
                    <th className="px-3 py-2">Stage</th>
                    <th className="px-3 py-2">Procedure</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  <tr className={planId === 0 ? 'bg-blue-50' : ''}>
                    <td className="px-3 py-2">
                      <input
                        type="radio"
                        name="merge-plan"
                        value="0"
                        checked={planId === 0}
                        onChange={() => setPlanId(0)}
                      />
                    </td>
                    <td className="px-3 py-2 font-semibold text-slate-700">None</td>
                    <td className="px-3 py-2 text-slate-400">—</td>
                    <td className="px-3 py-2 text-slate-400">—</td>
                    <td className="px-3 py-2 text-slate-400">—</td>
                  </tr>
                  {plans.map((plan) => (
                    <tr
                      key={plan.planId}
                      className={planId === plan.planId ? 'bg-blue-50' : ''}
                      onClick={() => setPlanId(plan.planId)}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="radio"
                          name="merge-plan"
                          value={plan.planId}
                          checked={planId === plan.planId}
                          onChange={() => setPlanId(plan.planId)}
                        />
                      </td>
                      <td className="px-3 py-2">{plan.name || `Plan #${plan.planId}`}</td>
                      <td className="px-3 py-2">{formatPlanDate(plan.lastContact)}</td>
                      <td className="px-3 py-2">{plan.stageName || '—'}</td>
                      <td className="px-3 py-2">
                        {[plan.proc1Name, plan.proc2Name].filter(Boolean).join(', ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
